// Offline phase 2 (PMU-271), the staff phone's side: what reached this phone from the door tablet
// over the school's Wi-Fi (records from any device, answers to door requests), merged into the
// screens. With the internet up the server's view is complete and newer; with it down, this is how
// a door request reaches the staff and a handover on one device shows on the others.
import { useSyncExternalStore } from "react";
import { presenceOf } from "@vavr/core";
import { LAN_PING_MS, LanClient, lanToday, type LanAnswer, type LanEvent, type LanMessage, type LanOutgoing, type LanRadioOffer, type Peer, type Roster } from "@vavr/client";
import type { LanLinkBridge } from "../shared/platform.ts";
import type { Incident } from "../shared/emergency.ts";
import type { Board, BoardChild } from "./board.ts";
import { isOpen, type PickupRequest } from "./pickup.ts";

interface LanState { connected: boolean; doors: string[]; events: LanEvent[]; answers: LanAnswer[] }
let state: LanState = { connected: false, doors: [], events: [], answers: [] };
const subs = new Set<() => void>();
const set = (s: Partial<LanState>) => { state = { ...state, ...s }; for (const f of subs) f(); };
let sender: ((m: LanOutgoing) => Promise<boolean>) | null = null;

export const lanState = () => state;
export function useLan(): LanState { return useSyncExternalStore((f) => { subs.add(f); return () => { subs.delete(f); }; }, lanState, lanState); }

/** TeacherRoot wires the live links here: the doors linked now, and how to send to them. */
export function lanConnected(doors: string[], send: typeof sender) { sender = doors.length ? send : null; set({ connected: doors.length > 0, doors }); }
/** A message from the door: kept (the day's), newest last. */
export function lanReceived(m: LanMessage) {
  // Only the day's: a phone left running overnight drops yesterday's (LAN_MAX_AGE_MS).
  const day = lanToday(state);
  if (m.k === "event" && !day.events.some((e) => e.pre.id === m.ev.pre.id)) set({ ...lanToday({ events: [...day.events, m.ev], answers: day.answers }) });
  if (m.k === "answer") set({ ...lanToday({ events: day.events, answers: [...day.answers.filter((a) => !(a.request_id === m.answer.request_id && a.by === m.answer.by)), m.answer] }) });
}
/** Sends this phone's own record or answer to the door (and through it to the other devices). */
export async function lanPublish(m: LanOutgoing) {
  if (m.k === "event") lanReceived(m);
  if (m.k === "answer") lanReceived(m);
  return sender ? sender(m).catch(() => false) : false;
}

/** The board with records heard over the Wi-Fi applied, where they are newer than what it shows. */
export function overlayBoard<B extends Board>(board: B, all = state.events): B {
  const events = lanToday({ events: all, answers: [] }).events;
  if (!events.length) return board;
  const byChild = new Map<string, BoardChild>(board.children.map((c) => [c.id, c]));
  for (const ev of [...events].sort((a, b) => a.pre.device_ts - b.pre.device_ts)) {
    const c = ev.pre.child_id ? byChild.get(ev.pre.child_id) : null;
    const moved = presenceOf(ev.pre);
    if (!c || !moved || (c.last_event && c.last_event.server_ts >= ev.pre.device_ts)) continue;
    byChild.set(c.id, {
      ...c, state: moved.state, custodian: moved.custodian,
      last_event: { seq: 0, type: ev.pre.type, adult_id: ev.pre.adult_id, claimed_name: ev.pre.claimed_name ?? null, server_ts: ev.pre.device_ts, server_flags: ["lan"] },
    });
  }
  return { ...board, children: board.children.map((c) => byChild.get(c.id)!) };
}

/** Children released during an emergency reunification (heard on the mesh) shown as gone home. */
export function overlayReleases<B extends Board>(board: B, incident: Incident | null): B {
  const released = incident?.released ?? {};
  if (!Object.keys(released).length) return board;
  return {
    ...board,
    children: board.children.map((c) => {
      const r = released[c.id];
      if (!r || (c.last_event && c.last_event.server_ts >= r.at)) return c;
      return { ...c, state: "out", custodian: null, last_event: { seq: 0, type: "pickup", adult_id: r.adultId, claimed_name: null, server_ts: r.at, server_flags: ["reunification", "mesh"] } };
    }),
  };
}

/**
 * The server's requests plus door requests heard over the Wi-Fi that the server does not have yet,
 * with answers and releases from any device applied.
 */
export function mergeRequests(list: PickupRequest[], roster: Roster | null, given: LanState = state): PickupRequest[] {
  const s = lanToday(given);
  let out = [...list];
  for (const ev of s.events) {
    const p = ev.pre;
    if ((p.type !== "pickup_request" && p.type !== "dropoff_request") || !p.child_id || out.some((r) => r.id === p.id)) continue;
    const child = roster?.children.find((c) => c.id === p.child_id);
    if (!child) continue; // not a child this phone may see (a teacher's phone has its rooms only)
    const adult = p.adult_id ? roster?.adults.find((a) => a.id === p.adult_id) : null;
    // As on the server, a new request for the same child replaces one still waiting.
    const kind = p.type === "dropoff_request" ? "dropoff" : "pickup";
    out = out.filter((r) => !(isOpen(r) && r.child.id === p.child_id && (r.kind ?? "pickup") === kind && r.created_at < p.device_ts));
    out.push({
      id: p.id, seq: 0, group_id: p.group_id, device_id: (p.data?.door_device as string | undefined) ?? p.device_id,
      kind: p.type === "dropoff_request" ? "dropoff" : "pickup", route: "room", room_id: child.room_id,
      room_name: roster?.rooms.find((r) => r.id === child.room_id)?.name ?? null, status: "open",
      staff_id: null, staff_name: null, note: null, escalated: false, released_seq: null, created_at: p.device_ts, updated_at: p.device_ts, server_flags: ["lan"],
      child: { id: child.id, first: child.first, last: child.last, photo_sha256: child.photo_sha256 },
      adult: adult ? { id: adult.id, first: adult.first, last: adult.last, photo_sha256: adult.photo_sha256, relation: roster?.links.find((l) => l.child_id === child.id && l.adult_id === adult.id)?.relation ?? null } : null,
      pre: { adult_id: p.adult_id, claimed_name: p.claimed_name, id_check: p.id_check, photo_sha256: p.photo_sha256, face: p.face, stroke_sha256: p.stroke_sha256, attestation: p.attestation, auth_method: p.auth_method, flags: p.flags, data: p.data, device_ts: p.device_ts } as PickupRequest["pre"],
      restrictions: (roster?.restrictions ?? []).filter((r) => r.child_id === child.id).map((r) => ({ id: r.id, kind: r.kind, never_release: r.never_release, subject_adult_id: r.subject_adult_id, subject_name: r.subject_name, notes: r.notes })),
    });
  }
  return out.map((r) => {
    // Released on any device (a pickup or drop-off naming this request), else the newest answer.
    if (s.events.some((e) => (e.pre.type === "pickup" || e.pre.type === "dropoff") && e.pre.data?.request_id === r.id)) return { ...r, status: "released" as const };
    const a = s.answers.filter((x) => x.request_id === r.id && x.at > r.updated_at).sort((x, y) => y.at - x.at)[0];
    return a ? { ...r, status: a.status as PickupRequest["status"], note: a.note ?? r.note, updated_at: a.at } : r;
  });
}


type Outgoing = LanOutgoing;
interface DoorLink { host: string; port: number; conn: string; client: LanClient | null; door: string | null; fails: number; radio: LanRadioOffer | null; retry?: ReturnType<typeof setTimeout> }

/** The door relay a phone's radio uses while the school is offline (phase 3). */
export interface LanRadioChoice { host: string; port: number; key: string; door: string }

/**
 * Of the linked doors offering a radio relay, the one the school's radios meet on while offline:
 * the offline door with the lowest id, so every phone on the Wi-Fi picks the same one. None while
 * a linked door still reaches the cloud (the cloud radio still works) or none is linked.
 */
export function chooseRadio(doors: { host: string; door: string | null; radio: LanRadioOffer | null }[]): LanRadioChoice | null {
  const linked = doors.filter((d) => d.door && d.radio);
  if (!linked.length || linked.some((d) => d.radio!.cloud)) return null;
  const d = linked.sort((a, b) => (a.door! < b.door! ? -1 : 1))[0]!;
  return { host: d.host, port: d.radio!.port, key: d.radio!.key, door: d.door! };
}

/**
 * The phone's links to every door tablet of the school on the Wi-Fi (a school may have several,
 * and each door's requests come from that door). One session per door, found by NSD, each
 * checked and encrypted by LanClient; this phone's records and answers go to all of them, and a
 * dropped link comes back on its own.
 */
export class DoorLinks {
  private doors = new Map<string, DoorLink>();
  private n = 0;
  private stopped = false;
  private offs: (() => void)[] = [];
  private timer: ReturnType<typeof setInterval> | undefined;
  private o: {
    link: LanLinkBridge; siteId: string; deviceId: string; privateKey: CryptoKey; peers: () => Peer[];
    onMessage: (m: LanMessage) => void; onChange: (doors: string[]) => void; retryMs?: number; pingMs?: number;
    /** The radio relay to use, whenever the choice changes (phase 3). */
    onRadio?: (r: LanRadioChoice | null) => void;
    /** A door linking to the other doors (for the radio) skips itself. */
    skipDoor?: string;
    /** Offers known besides the linked doors' (a door's own), for the radio choice. */
    ownRadio?: () => { host: string; door: string; radio: LanRadioOffer | null } | null;
  };
  /** The choice again (a door's own offer changed). */
  refreshRadio() { this.radioChanged(); }
  private radioKey = "";
  private radioChanged() {
    const own = this.o.ownRadio?.();
    const r = chooseRadio([...this.doors.values(), ...(own ? [own] : [])]);
    const key = r ? `${r.host}:${r.port}:${r.key}` : "";
    if (key === this.radioKey) return;
    this.radioKey = key;
    this.o.onRadio?.(r);
  }
  constructor(o: DoorLinks["o"]) { this.o = o; }

  start() {
    const { link } = this.o;
    this.offs = [
      link.on("found", (d) => { if (!this.o.skipDoor || d.door !== this.o.skipDoor) this.found(d.host, d.port); }),
      link.on("open", (d) => this.opened(d.conn)),
      link.on("message", (d) => { void this.byConn(d.conn)?.client?.received(d.text); }),
      link.on("close", (d) => this.closed(d.conn)),
    ];
    void link.discover(this.o.siteId);
    this.timer = setInterval(() => this.tick(), this.o.pingMs ?? LAN_PING_MS);
  }

  /** Pings each linked door; a link silent too long is closed (and comes back on its own). */
  tick(now = Date.now()) {
    for (const d of this.doors.values()) if (d.client && d.client.tick(now)) void this.o.link.close(d.conn);
  }

  stop() {
    this.stopped = true;
    clearInterval(this.timer);
    for (const d of this.doors.values()) clearTimeout(d.retry);
    this.offs.forEach((f) => f());
    void this.o.link.stop();
    this.doors.clear();
    this.o.onChange([]);
    this.radioChanged();
  }

  /** The doors linked now. */
  linked() { return [...this.doors.values()].filter((d) => d.client?.ready && d.door).map((d) => d.door!); }

  /** To every linked door; true if at least one took it. */
  async publish(m: Outgoing) {
    const ready = [...this.doors.values()].filter((d) => d.client?.ready);
    const sent = await Promise.all(ready.map((d) => d.client!.publish(m)));
    return sent.some(Boolean);
  }

  private found(host: string, port: number) {
    const d = this.doors.get(host);
    if (d) { d.port = port; if (!d.client && !d.retry) this.connect(d); return; }
    const fresh: DoorLink = { host, port, conn: "", client: null, door: null, fails: 0, radio: null };
    this.doors.set(host, fresh);
    this.connect(fresh);
  }

  private connect(d: DoorLink) {
    if (this.stopped) return;
    d.conn = `door${++this.n}`;
    d.client = null;
    void this.o.link.connect(d.host, d.port, d.conn);
  }

  private byConn(conn: string) { for (const d of this.doors.values()) if (d.conn === conn) return d; return null; }

  private opened(conn: string) {
    const d = this.byConn(conn);
    if (!d) return;
    d.client = new LanClient({
      siteId: this.o.siteId, deviceId: this.o.deviceId, privateKey: this.o.privateKey, peers: this.o.peers,
      send: (t) => { void this.o.link.send(conn, t); }, close: () => { void this.o.link.close(conn); },
      onMessage: (m) => { if (m.k === "radio") { d.radio = { key: m.key, port: m.port, cloud: m.cloud }; this.radioChanged(); } else this.o.onMessage(m); },
      onReady: (door) => { d.door = door; d.fails = 0; this.o.onChange(this.linked()); },
    });
  }

  private closed(conn: string) {
    const d = this.byConn(conn);
    if (!d) return;
    const was = d.door !== null && d.client?.ready;
    d.client = null; d.door = null; d.radio = null;
    if (was) this.o.onChange(this.linked());
    this.radioChanged();
    if (this.stopped) return;
    // A door that keeps failing (switched off, or a new address) is forgotten; discovery finds it again.
    if (++d.fails > 6) { this.doors.delete(d.host); void this.o.link.discover(this.o.siteId); return; }
    d.retry = setTimeout(() => { d.retry = undefined; this.connect(d); }, this.o.retryMs ?? 5000);
  }
}
